import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSearch } from "Context/SearchContext";
import { useEcomConfig } from "hooks/useEcomConfig";
import { useSearchAnalytics } from "Context/SearchAnalyticsContext";

const SearchBar: React.FC = () => {
  const { showAuthor } = useEcomConfig();
  const { searchTerm, setSearchTerm } = useSearch();
  const { queueAnalytics } = useSearchAnalytics();
  const [inputValue, setInputValue] = useState(searchTerm);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (searchTerm !== inputValue) {
      setInputValue(searchTerm);
    }
  }, [searchTerm]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlSearch = params.get("search");

    // If there is a search term in the URL, sync it to state
    if (urlSearch) {
      const decodedSearch = decodeURIComponent(urlSearch);
      if (decodedSearch !== searchTerm) {
        setSearchTerm(decodedSearch);
        setInputValue(decodedSearch);
      }
    } else {
      // If no search in URL and we are on the listing page, clear it
      if (location.pathname.includes("/books-listing")) {
        if (searchTerm !== "" || inputValue !== "") {
          setSearchTerm("");
          setInputValue("");
        }
      } else {
        const isSearchContext =
          location.pathname.includes("/books-listing") ||
          location.pathname.includes("/product") ||
          location.pathname.includes("/s"); // s is search view

        if (!isSearchContext || location.pathname === "/") {
          if (searchTerm !== "" || inputValue !== "") {
            setSearchTerm("");
            setInputValue("");
          }
        }
      }
    }
  }, [location.pathname, location.search]);

  const handleSubmit = () => {
    if (inputValue.trim()) {
      queueAnalytics("SEARCH_BAR", inputValue.trim());
      setSearchTerm(inputValue.trim());
      navigate(
        `/books-listing?search=${encodeURIComponent(inputValue.trim())}`,
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <div className="relative w-full">
      <input
        type="text"
        placeholder={
          showAuthor
            ? "Search by ISBN, Book Name, or Author Name"
            : "Search by ISBN or Book Name"
        }
        className="w-full bg-vintageBg text-black placeholder:text-gray-700 border border-gray-700 border-opacity-50 rounded-xl px-2 lg:px-5 py-2 lg:py-3 focus:outline-none focus:ring-0"
        value={inputValue}
        onChange={(e) => {
          const val = e.target.value;
          setInputValue(val);
          if (!val.trim()) {
            setSearchTerm("");
            const params = new URLSearchParams(location.search);
            if (params.has("search")) {
              params.delete("search");
              navigate(`${location.pathname}?${params.toString()}`);
            }
          }
        }}
        onKeyDown={handleKeyDown}
      />
      <div
        className="absolute right-2 top-0 h-full flex items-center text-gray-700 text-opacity-50 cursor-pointer hover:text-vintageText transition-colors"
        onClick={handleSubmit}
        tabIndex={0}
        role="button"
        aria-label="Search"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleSubmit();
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-search"
        >
          <path d="m21 21-4.34-4.34" />
          <circle cx="11" cy="11" r="8" />
        </svg>
      </div>
    </div>
  );
};

export default SearchBar;
